import { useEffect, useRef } from "react";
import { db } from "@/firebase/client";
import {
  parseReferralFromUrl,
  getReferralData,
  setReferralCookie,
  ReferralData,
} from "@/lib/referral-utils";

/**
 * Hook that captures referral data from URL and stores it in cookies/localStorage
 * Should be used at the app root level to capture referrals on any landing page
 */
export function useReferralTracking(): void {
  const hasTracked = useRef(false);

  useEffect(() => {
    // Only run once per page load
    if (hasTracked.current) return;
    hasTracked.current = true;

    const trackReferral = async () => {
      try {
        // Check if there's already a valid referral stored
        const existingReferral = getReferralData();
        if (existingReferral) {
          console.log("Existing referral found:", existingReferral.code);
          return; // Don't overwrite existing referral
        }

        // Parse URL for referral data
        const urlReferral = parseReferralFromUrl();
        if (!urlReferral || !urlReferral.code) {
          return; // No referral in URL
        }

        console.log("Found referral code in URL:", urlReferral.code);

        // Validate the affiliate code exists and is active
        const isValid = await validateAffiliateCode(urlReferral.code);
        if (!isValid) {
          console.log("Invalid or inactive affiliate code:", urlReferral.code);
          return;
        }

        // Store the referral
        const referralData: ReferralData = {
          code: urlReferral.code,
          timestamp: Date.now(),
          landingPage: window.location.pathname,
          utmSource: urlReferral.utmSource,
          utmMedium: urlReferral.utmMedium,
          utmCampaign: urlReferral.utmCampaign,
        };

        setReferralCookie(referralData);
        console.log("Referral tracked successfully:", referralData);

        // Optionally clean the URL (remove ref param)
        cleanUrlParams();
      } catch (error) {
        console.error("Error tracking referral:", error);
      }
    };

    trackReferral();
  }, []);
}

/**
 * Validates that an affiliate code exists and belongs to an active affiliate
 */
async function validateAffiliateCode(code: string): Promise<boolean> {
  try {
    // Query affiliates collection for this code
    // We need to search by affiliateCode field
    const { collection, query, where, getDocs } = await import("firebase/firestore");

    const affiliatesRef = collection(db, "affiliates");
    const q = query(
      affiliatesRef,
      where("affiliateCode", "==", code.toUpperCase()),
      where("status", "==", "active")
    );

    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error("Error validating affiliate code:", error);
    // In case of error, accept the code (we'll validate again on signup)
    return true;
  }
}

/**
 * Removes referral-related parameters from URL without page reload
 */
function cleanUrlParams(): void {
  try {
    const url = new URL(window.location.href);
    const paramsToRemove = ["ref", "affiliate", "utm_source", "utm_medium", "utm_campaign"];

    let hasParams = false;
    for (const param of paramsToRemove) {
      if (url.searchParams.has(param)) {
        hasParams = true;
        url.searchParams.delete(param);
      }
    }

    if (hasParams) {
      window.history.replaceState({}, "", url.toString());
    }
  } catch (error) {
    // Ignore errors - URL cleaning is optional
  }
}

/**
 * Hook to get current referral data (for display purposes)
 */
export function useReferralData(): ReferralData | null {
  return getReferralData();
}
